/**
 * G2eGente - Aplicar DISC para Funcionário
 * Reutiliza o questionário DISC existente para candidatos
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI, discAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, 
  Loader2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Check,
  History,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

// Escala Likert
const LIKERT_OPTIONS = [
  { value: 1, label: 'Discordo totalmente' },
  { value: 2, label: 'Discordo parcialmente' },
  { value: 3, label: 'Neutro' },
  { value: 4, label: 'Concordo parcialmente' },
  { value: 5, label: 'Concordo totalmente' }
];

// Perguntas DISC (20 perguntas)
const DISC_QUESTIONS = [
  { id: 1, text: 'Gosto de assumir o controle em situações desafiadoras', disc: 'D' },
  { id: 2, text: 'Sou direto(a) e objetivo(a) ao me comunicar', disc: 'D' },
  { id: 3, text: 'Prefiro resultados rápidos a processos demorados', disc: 'D' },
  { id: 4, text: 'Tenho facilidade em tomar decisões difíceis', disc: 'D' },
  { id: 5, text: 'Fico entediado(a) facilmente com tarefas repetitivas', disc: 'D' },
  { id: 6, text: 'Gosto de interagir e conhecer novas pessoas', disc: 'I' },
  { id: 7, text: 'Sou entusiasmado(a) e otimista naturalmente', disc: 'I' },
  { id: 8, text: 'Prefiro trabalhar em equipe do que sozinho(a)', disc: 'I' },
  { id: 9, text: 'Tenho facilidade para motivar e inspirar outros', disc: 'I' },
  { id: 10, text: 'Valorizo o reconhecimento e a aprovação social', disc: 'I' },
  { id: 11, text: 'Prefiro ambientes de trabalho estáveis e previsíveis', disc: 'S' },
  { id: 12, text: 'Sou paciente e bom(boa) ouvinte', disc: 'S' },
  { id: 13, text: 'Valorizo a harmonia e evito conflitos', disc: 'S' },
  { id: 14, text: 'Sou leal e comprometido(a) com minha equipe', disc: 'S' },
  { id: 15, text: 'Prefiro mudanças graduais a transformações bruscas', disc: 'S' },
  { id: 16, text: 'Sou detalhista e perfeccionista no meu trabalho', disc: 'C' },
  { id: 17, text: 'Prefiro seguir regras e procedimentos estabelecidos', disc: 'C' },
  { id: 18, text: 'Analiso cuidadosamente antes de tomar decisões', disc: 'C' },
  { id: 19, text: 'Valorizo qualidade acima de velocidade', disc: 'C' },
  { id: 20, text: 'Sou crítico(a) e questiono informações recebidas', disc: 'C' }
];

const DISC_COLORS = {
  D: { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700', name: 'Dominância' },
  I: { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-700', name: 'Influência' },
  S: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700', name: 'Estabilidade' },
  C: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700', name: 'Conformidade' }
};

export default function EmployeeDISC() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const fetchData = async () => {
    try {
      let empData;
      
      try {
        const empRes = await employeesAPI.getById(employeeId);
        empData = empRes.data;
      } catch (empError) {
        console.error('Erro ao carregar funcionário:', empError);
        if (empError.response?.status === 404) {
          toast.error('Funcionário não encontrado');
        } else {
          toast.error('Erro ao carregar funcionário');
        }
        navigate('/company/employees');
        return;
      }
      
      const histRes = await employeesAPI.getDISCHistory(employeeId);
      setEmployee(empData);
      setHistory(histRes.data.history || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do funcionário');
      navigate('/company/employees');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const calculateResult = () => {
    // Calcular pontuação por dimensão
    const scores = { D: 0, I: 0, S: 0, C: 0 };
    const counts = { D: 0, I: 0, S: 0, C: 0 };
    
    DISC_QUESTIONS.forEach(q => {
      if (answers[q.id]) {
        scores[q.disc] += answers[q.id];
        counts[q.disc]++;
      }
    });
    
    // Calcular porcentagens
    const total = Object.values(scores).reduce((a, b) => a + b, 0);
    const percentages = {};
    Object.keys(scores).forEach(key => {
      percentages[key] = Math.round((scores[key] / total) * 100);
    });
    
    // Determinar perfil dominante
    const sorted = Object.entries(percentages).sort((a, b) => b[1] - a[1]);
    const dominant = sorted[0][0];
    const secondary = sorted[1][0];
    const profile = percentages[secondary] >= 20 ? `${dominant}${secondary}` : dominant;
    
    const descriptions = {
      D: 'Focado em resultados, direto e decisivo. Assume riscos e lidera naturalmente.',
      I: 'Comunicativo e entusiasmado. Motiva pessoas e cria conexões facilmente.',
      S: 'Colaborativo e paciente. Valoriza estabilidade e trabalho em equipe.',
      C: 'Analítico e preciso. Foca em qualidade e segue processos rigorosos.',
      DI: 'Líder carismático. Combina foco em resultados com habilidade de influenciar.',
      DC: 'Líder analítico. Une decisão rápida com análise criteriosa.',
      ID: 'Influenciador assertivo. Motiva e direciona equipes com entusiasmo.',
      IS: 'Comunicador empático. Conecta pessoas mantendo harmonia.',
      SD: 'Executor confiável. Estabilidade com capacidade de decisão quando necessário.',
      SI: 'Apoiador motivador. Cria ambientes harmoniosos e estimulantes.',
      SC: 'Especialista consistente. Combina paciência com atenção aos detalhes.',
      CD: 'Analista decisor. Precisão técnica com assertividade.',
      CI: 'Comunicador técnico. Explica conceitos complexos de forma acessível.',
      CS: 'Perfeccionista colaborativo. Qualidade com foco em equipe.'
    };
    
    return {
      disc_profile: profile,
      disc_detalhado: {
        D: percentages.D,
        I: percentages.I,
        S: percentages.S,
        C: percentages.C,
        resultado_dominante: profile,
        texto_explicativo: descriptions[profile] || descriptions[dominant]
      }
    };
  };

  const handleComplete = async () => {
    const discResult = calculateResult();
    setResult(discResult);
    
    setSaving(true);
    try {
      await employeesAPI.saveDISC(employeeId, discResult);
      toast.success('Perfil DISC salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar perfil DISC');
    } finally {
      setSaving(false);
    }
  };

  const progress = (Object.keys(answers).length / DISC_QUESTIONS.length) * 100;
  const currentQ = DISC_QUESTIONS[currentQuestion];
  const canProceed = answers[currentQ?.id];
  const isLastQuestion = currentQuestion === DISC_QUESTIONS.length - 1;
  const allAnswered = Object.keys(answers).length === DISC_QUESTIONS.length;

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="employee-disc">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/company/employees')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Avaliação DISC</h1>
              <p className="text-slate-600">{employee?.nome} - {employee?.cargo}</p>
            </div>
          </div>
          {history.length > 0 && (
            <Button 
              variant="outline"
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="w-4 h-4 mr-2" />
              Histórico ({history.length})
            </Button>
          )}
        </div>

        {/* History Panel */}
        {showHistory && history.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Histórico de Avaliações DISC</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((h, i) => (
                  <div key={h.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className={`${DISC_COLORS[h.disc_profile?.[0]]?.light} ${DISC_COLORS[h.disc_profile?.[0]]?.text}`}>
                        {h.disc_profile}
                      </Badge>
                      <span className="text-sm text-slate-600">
                        {new Date(h.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {Object.entries(h.disc_detalhado || {}).filter(([k]) => ['D','I','S','C'].includes(k)).map(([k, v]) => (
                        <span key={k} className={`px-2 py-1 rounded ${DISC_COLORS[k]?.light} ${DISC_COLORS[k]?.text}`}>
                          {k}: {v}%
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current DISC Profile */}
        {employee?.disc_profile && !started && !result && (
          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 ${DISC_COLORS[employee.disc_profile[0]]?.bg} rounded-xl flex items-center justify-center`}>
                  <span className="text-2xl font-bold text-white">{employee.disc_profile}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Perfil Atual: {employee.disc_profile}</h3>
                  <p className="text-sm text-slate-600">{employee.disc_detalhado?.texto_explicativo}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Última avaliação: {employee.disc_updated_at ? new Date(employee.disc_updated_at).toLocaleDateString('pt-BR') : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Start Screen */}
        {!started && !result && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                {employee?.disc_profile ? 'Reavaliar Perfil DISC' : 'Avaliar Perfil DISC'}
              </h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                O questionário contém {DISC_QUESTIONS.length} perguntas e leva aproximadamente 5 minutos. 
                As respostas ajudam a identificar o perfil comportamental do funcionário.
              </p>
              <Button 
                onClick={() => setStarted(true)}
                className="bg-purple-600 hover:bg-purple-700"
                size="lg"
              >
                <Brain className="w-5 h-5 mr-2" />
                Iniciar Avaliação
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Questionnaire */}
        {started && !result && (
          <Card className="border-slate-200">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Pergunta {currentQuestion + 1} de {DISC_QUESTIONS.length}
                </CardTitle>
                <Badge className={`${DISC_COLORS[currentQ.disc].light} ${DISC_COLORS[currentQ.disc].text}`}>
                  {DISC_COLORS[currentQ.disc].name}
                </Badge>
              </div>
              <Progress value={progress} className="h-2 mt-3" />
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-lg text-slate-900 mb-8 text-center font-medium">
                "{currentQ.text}"
              </p>
              
              <div className="space-y-3">
                {LIKERT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQ.id, option.value)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      answers[currentQ.id] === option.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option.label}</span>
                      {answers[currentQ.id] === option.value && (
                        <Check className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                  disabled={currentQuestion === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                
                {isLastQuestion ? (
                  <Button
                    onClick={handleComplete}
                    disabled={!allAnswered || saving}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Finalizar
                  </Button>
                ) : (
                  <Button
                    onClick={() => setCurrentQuestion(currentQuestion + 1)}
                    disabled={!canProceed}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {result && (
          <Card className="border-slate-200">
            <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-cyan-50">
              <CardTitle className="flex items-center gap-3">
                <div className={`w-12 h-12 ${DISC_COLORS[result.disc_profile[0]]?.bg} rounded-xl flex items-center justify-center`}>
                  <span className="text-xl font-bold text-white">{result.disc_profile}</span>
                </div>
                <div>
                  <span className="text-xl">Resultado: Perfil {result.disc_profile}</span>
                  <p className="text-sm font-normal text-slate-600 mt-1">
                    {result.disc_detalhado.texto_explicativo}
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-6">
                <h4 className="font-semibold text-slate-900 mb-4">Distribuição do Perfil</h4>
                <div className="grid grid-cols-2 gap-4">
                  {['D', 'I', 'S', 'C'].map(dim => (
                    <div key={dim} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${DISC_COLORS[dim].text}`}>
                          {dim} - {DISC_COLORS[dim].name}
                        </span>
                        <span className="font-bold">{result.disc_detalhado[dim]}%</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${DISC_COLORS[dim].bg} rounded-full transition-all duration-500`}
                          style={{ width: `${result.disc_detalhado[dim]}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setResult(null);
                    setStarted(false);
                    setAnswers({});
                    setCurrentQuestion(0);
                    fetchData();
                  }}
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Nova Avaliação
                </Button>
                <Button 
                  onClick={() => navigate('/company/employees')}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  Voltar para Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
